import {
  useInterviewFeedbackMutation,
  useFeedbackScreenQuery,
} from "@/features/interview/interviewApi";
import { CoIntervuelogo } from "@assets/Icons";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
  Typography,
} from "@cointervue/design-system";
import { CheckCircle, MessageSquare, Star } from "lucide-react";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import PreInterviewScreenSkeleton from "@/components/fallbacks/PreInterviewScreenSkeleton";
import type { AfterInterviewProps } from "./types";

const DEFAULT_NEXT_STEPS = [
  "Our AI is analyzing your responses and generating insights",
  "The hiring team will review your interview within 2–3 business days",
  "You'll receive an email with next steps",
];

const LEAVE_NEXT_STEPS = [
  "Your current progress has been safely stored",
  "You can return to this interview anytime using the same link",
  "The interview will resume exactly where you left off",
];

function AfterInterview({
  title: propTitle,
  description: propDescription,
  icon: Icon = CheckCircle,
  iconColor = "text-brand-green",
  iconBgColor = "bg-brand-light-green",
  showFeedback: propShowFeedback = true,
  showNextSteps: propShowNextSteps = true,
  nextSteps: propNextSteps,
  jobTitle = "Senior Frontend Developer",
  isPanelist = false,
  children,
}: AfterInterviewProps) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isLeave = searchParams.get("reason") === "leave";

  const title =
    propTitle ||
    (isPanelist
      ? "Thank you for joining!"
      : isLeave
        ? "Progress Saved!"
        : "Interview Complete!");

  const description =
    propDescription ||
    (isPanelist
      ? isLeave
        ? "The interview is still ongoing. You can rejoin at any time using the original link."
        : "The interview session has concluded. You may now close this window."
      : isLeave
        ? "You have successfully left the interview. Your progress has been saved and you can rejoin at any time using the original link."
        : undefined);

  const showFeedback = isPanelist ? false : isLeave ? false : propShowFeedback;
  const showNextSteps = propNextSteps
    ? propShowNextSteps
    : isPanelist
      ? false
      : propShowNextSteps;

  const nextSteps = isLeave
    ? LEAVE_NEXT_STEPS
    : propNextSteps || DEFAULT_NEXT_STEPS;

  const isConcludeRoute = location.pathname.includes("conclude");

  const { data: feedbackData, isFetching: isFeedbackLoading } =
    useFeedbackScreenQuery(undefined, {
      skip: !isConcludeRoute || isLeave || !propShowFeedback || isPanelist,
    });

  const stars = Array.from({ length: 5 }, (_, i) => i + 1);

  const [rating, setRating] = useState(0);

  const [submitFeedback, { isLoading, isSuccess }] =
    useInterviewFeedbackMutation();

  if (isFeedbackLoading) {
    return <PreInterviewScreenSkeleton />;
  }

  const handleSubmit = async () => {
    try {
      await submitFeedback({ rating }).unwrap();
      console.log("Feedback submitted successfully");
    } catch (error) {
      console.error("Feedback failed", error);
    }
  };

  return (
    <div className="h-dvh w-full flex items-center flex-col px-3 sm:px-0 overflow-y-auto py-6">
      <div className="my-auto flex flex-col items-center w-full">
        <img src={CoIntervuelogo} alt="CoIntervue Logo" className="w-36 pb-3" />
        <Card className="w-full max-w-sm rounded-2xl shadow-lg">
          <CardHeader className="text-center space-y-1 p-3">
            {/* Success Icon */}
            <div className="flex justify-center">
              <div
                className={cn(
                  "h-12 w-12 rounded-full flex items-center justify-center",
                  iconBgColor,
                )}
              >
                <Icon className={cn("h-7 w-7", iconColor)} />
              </div>
            </div>

            <CardTitle>
              {feedbackData?.data?.lifeCycleState && !isPanelist
                ? `Interview ${feedbackData.data.lifeCycleState}`
                : title}
            </CardTitle>
            <CardDescription>
              {description || (
                <>
                  Thank you for completing your interview for{" "}
                  <span className="font-medium text-brand-black">
                    {feedbackData?.data?.jobTitle || jobTitle}
                  </span>
                </>
              )}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-2 p-4">
            {/* Info Box */}
            {showNextSteps && (
              <div className="rounded-2xl bg-brand-gray-light/40 p-5">
                {/* Heading */}
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="h-4 w-4 text-brand-blue" />
                  <Typography
                    variant="bodySmall"
                    weight="semibold"
                    className="text-brand-black"
                  >
                    What happens next?
                  </Typography>
                </div>

                {/* Bullet Points */}
                <ul className="list-disc pl-5 space-y-2 marker:text-brand-blue">
                  {nextSteps.map((step, index) => (
                    <li key={index}>
                      <Typography variant="bodySmall">{step}</Typography>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Rating Section */}
            {showFeedback && (
              <div className="text-center space-y-3 bg-brand-light-blue-2/50 p-4 rounded-xl">
                <Typography variant="bodySmall">
                  How was your interview experience?
                </Typography>

                <div className="flex justify-center gap-2">
                  {stars.map((star) => (
                    <Star
                      key={star}
                      onClick={() => !isSuccess && setRating(star)}
                      className={cn(
                        "h-6 w-6 transition-colors",
                        !isSuccess && "cursor-pointer",
                        rating >= star
                          ? "text-brand-blue fill-brand-blue"
                          : "text-muted-foreground",
                      )}
                    />
                  ))}
                </div>

                <Button
                  className="w-full rounded-full"
                  variant="interviewPrimaryAction"
                  disabled={rating === 0 || isLoading || isSuccess}
                  onClick={handleSubmit}
                >
                  {isLoading
                    ? "Submitting..."
                    : isSuccess
                      ? "Submitted"
                      : "Submit Feedback"}
                </Button>
              </div>
            )}

            {children}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default AfterInterview;
